import React from 'react';
import { Card, CardContent, Typography, CircularProgress, Box, Alert } from '@mui/material';
import { Person as PersonIcon, Cake as CakeIcon, Wc as WcIcon, MailOutline as MailOutlineIcon, Phone as PhoneIcon } from '@mui/icons-material';
import { useGetPatientQuery } from '@/core/services/patient'; // Corrected import path
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

interface PatientCardProps {
    patientId: string;
}

export const PatientCard: React.FC<PatientCardProps> = ({ patientId }) => {
    const { data: patient, error, isLoading } = useGetPatientQuery(patientId);
    const navigate = useNavigate();

    const handleCardClick = () => {
        window.open(`/patients/${patientId}`, '_blank');
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                <Typography>Loading Patient Information...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">Error loading patient data. Please try again.</Alert>;
    }

    if (!patient) {
        return <Alert severity="warning">Patient with ID {patientId} not found.</Alert>;
    }

    const { userInfo, email, contactInfo } = patient;
    const formattedBirthday = userInfo?.birthday ? dayjs(userInfo.birthday).format('DD/MM/YYYY') : 'N/A';
    const age = userInfo?.birthday ? dayjs().diff(dayjs(userInfo.birthday), 'year') : null;

    return (
        <Card
            onClick={handleCardClick}
            sx={{
                cursor: 'pointer',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: 3,
                    transform: 'translateY(-2px)'
                }
            }}
        >
            <CardContent>
                <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PersonIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                    {userInfo?.name} {userInfo?.surname}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CakeIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                        <Typography variant="body2">
                            {formattedBirthday} {age !== null ? `(${age} years)` : ''}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <WcIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                        <Typography variant="body2">{userInfo?.gender || 'N/A'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <MailOutlineIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                        <Typography variant="body2">{email || 'N/A'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PhoneIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                        <Typography variant="body2">{contactInfo?.phone || 'N/A'}</Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default PatientCard; 